import React from 'react';

// Renders the full brand wordmark in the dashboard hero.
// Styles:
//   - "glow":  full-color wordmark on the dark hero with a teal/cyan glow behind it (default)
//   - "panel": full-color wordmark on a crisp white rounded plate (max readability)
//   - "plain": wordmark recolored to solid light, placed directly on the dark hero
export function HeroLogo({ settings, className = '' }) {
  const wordmark = settings?.logo_wordmark;
  const raw = settings?.hero_logo_style;
  const style = raw === 'panel' || raw === 'plain' ? raw : 'glow';
  const brandName = settings?.brand_name || 'Commissioning Tools';

  // Fallback: no wordmark uploaded yet -> keep the original text title
  if (!wordmark) {
    return (
      <h1 className={`font-display text-3xl sm:text-[34px] font-semibold tracking-tight text-[color:var(--cx-fg)] ${className}`} data-testid="hero-title">
        {brandName}
      </h1>
    );
  }

  if (style === 'panel') {
    return (
      <div
        data-testid="hero-logo-panel"
        className={`inline-flex items-center rounded-2xl bg-white px-6 py-4 border border-[color:var(--cx-border)] shadow-[var(--cx-shadow-soft)] ${className}`}
      >
        <img src={wordmark} alt={brandName} data-testid="hero-logo" data-style="panel"
          className="h-20 sm:h-24 lg:h-28 w-auto object-contain" />
      </div>
    );
  }

  if (style === 'plain') {
    return (
      <img
        src={wordmark}
        alt={brandName}
        data-testid="hero-logo"
        data-style="plain"
        className={`h-28 sm:h-32 lg:h-36 w-auto object-contain ${className}`}
        style={{ filter: 'brightness(0) invert(1)' }}
      />
    );
  }

  // "glow" — original colors on the dark hero, with a soft teal/cyan halo behind the mark
  return (
    <div className={`relative inline-flex items-center ${className}`} data-testid="hero-logo-glow">
      <span
        aria-hidden="true"
        className="pointer-events-none absolute -inset-x-16 -inset-y-12 rounded-[64px]"
        style={{
          background:
            'radial-gradient(closest-side, rgba(46,230,197,0.30), rgba(43,182,255,0.16) 58%, transparent 78%)',
          filter: 'blur(14px)',
        }}
      />
      <img
        src={wordmark}
        alt={brandName}
        data-testid="hero-logo"
        data-style="glow"
        className="relative h-28 sm:h-32 lg:h-36 w-auto object-contain"
      />
    </div>
  );
}
